// @ts-strict-ignore
import type { Viewport } from 'react-flow-renderer';
import type { WorkflowState } from './state';
import type {
  Action,
  PublicId,
  SchemaInstance,
  Threshold,
  WorkflowProps,
} from '../engine/types';
import type { Operation } from 'flow/service/changelog/types';
import type { Delta } from 'jsondiffpatch';

// todo - think about moving this to the graph dir
export type CanvasRect = {
  x: Viewport['x'];
  y: Viewport['y'];
  zoom: Viewport['zoom'];
  width: DOMRect['width'];
  height: DOMRect['height'];
};

export type CanvasProperties = {
  isMoving: boolean;
  rect: CanvasRect;
};

export type NodeConnectorInteractionState = 'hovered' | 'focused';
export type NodeConnectorIntent = 'link' | 'add';

export type DerivedNodeConnectorState = WorkflowState['ui']['connector'] & {
  /**
   * this field is related to the connector state, but can be derived from the existing
   * store values, so we do not keep it in the store.
   */
  intent: NodeConnectorIntent;
};

export type Snapshot<T> = {
  snapshotId: PublicId;
  data: T;
};

export type WorkflowSnapshots = {
  workflowData: {
    history: {
      initial: Snapshot<Partial<SchemaInstance>>;
      past: Snapshot<Partial<SchemaInstance>>[];
    };
  };
  workflowProps: {
    history: {
      initial: Snapshot<WorkflowProps>;
    };
  };
};

export const snippetMode = {
  pastePending: 'pastePending',
  preview: 'preview',
  review: 'review',
} as const;

export type SnippetMode = keyof typeof snippetMode;

export const snippetOperation = {
  insertSnippet: 'insertSnippet',
  insertLinkedSnippet: 'insertLinkedSnippet',
  upgradeLinkedSnippet: 'upgradeLinkedSnippet',
} as const;

export type SnippetOperation = keyof typeof snippetOperation;

export type SnippetActiveOperation = {
  operation: SnippetOperation | null;
  snippetActionPublicId: PublicId | null;
};

export type WorkflowCopyPasteSupport = {
  supported: boolean;
  reason: string;
};

export const snippetInsertionErrorType = {
  tagNameTooLong: 'tagNameTooLong',
} as const;

export type SnippetInsertionErrorType = keyof typeof snippetInsertionErrorType;

export type SnippetInsertionError = {
  type: SnippetInsertionErrorType;
  actionPublicId: PublicId;
};

// generated by jsonDiffPatch
export type WorkflowDiff = {
  workflowData: Delta;
  workflowProps: Delta | null;
};

// TBD add more descriptions
export const changeDescription = {
  action: 'action',
  threshold: 'threshold',
  group: 'group',
} as const;

export type ChangeDescription = keyof typeof changeDescription;

// ================ Ui Effect Context ================

/** Fields shared by every UI effect context, discriminated by `target`. */
type BaseUiEffectContext<TTarget extends ChangeDescription> = {
  target: TTarget;
};

export type ActionUiEffectContext = BaseUiEffectContext<typeof changeDescription.action> & {
  publicId: Action['publicId'];
};

export type ThresholdUiEffectContext = BaseUiEffectContext<typeof changeDescription.threshold> & {
  publicId: Threshold['publicId'];
  actionId: Threshold['actionId'];
};

export type GroupUiEffectContext = BaseUiEffectContext<typeof changeDescription.group> & {
  actionId: Threshold['actionId'];
};

export type UiEffectContext =
  | ActionUiEffectContext
  | ThresholdUiEffectContext
  | GroupUiEffectContext;

export type UiEffectContextByTarget = {
  [changeDescription.action]: ActionUiEffectContext;
  [changeDescription.threshold]: ThresholdUiEffectContext;
  [changeDescription.group]: GroupUiEffectContext;
};

export type WorkflowHistoryEntry = {
  diff: WorkflowDiff;
  operation: Operation;
  uiEffectContext: UiEffectContext;
};

/** A callback that mutates redux state by performing its own dispatch(es). */
export type WorkflowStateChange = () => void;
